import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getCurrentHandle,
  isConfirmedSelector,
} from 'reduxes/slices/current/selectors'
import { addParticipant, rejectParticipant } from 'reduxes/slices/topics/actions'

import { css } from 'design'
import { Button } from 'primitives/button'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import { useHandleMetadata } from '../../../hooks/use_handle_metadata'
import { makeGetAddContactRequest } from '../../../reduxes/slices/api/selectors'
import { getSelectedTopic } from '../../../reduxes/slices/topics/selectors'
import { useSelectorDelegateStore } from '../../../reduxes/stores/root_store/hooks'
import Timeline from '../timeline'
import {useAxios} from "../../../hooks/use_axios";

export default function AddContactRequest({ timeline }) {
  const dispatch = useDispatch()
  const axios = useAxios()
  const currentHandle = useSelector(getCurrentHandle)
  const getAddContactRequest = useMemo(makeGetAddContactRequest, [])
  const item = useSelector((state) =>
    getAddContactRequest(state, timeline?.tl_id),
  )
  const topic = useSelector(getSelectedTopic)
  const ownerHandle = topic?.created_by_handle
  const isConfirmed = useSelectorDelegateStore(isConfirmedSelector)
  const { name: nameOfRequester } = useHandleMetadata(item?.handle)
  const { name: nameOfPersonToAdd } = useHandleMetadata(item?.handle_to_add)
  const { name: nameOfTopicOwner } = useHandleMetadata(ownerHandle)
  const isTopicOwner = currentHandle === ownerHandle

  const accept = () => {
    dispatch(axios(addParticipant(topic, item.handle_to_add)))
  }

  const reject = () => {
    dispatch(axios(rejectParticipant(topic, item.id)))
  }

  if (!item) {
    return null
  }

  if (!item) {
    return null
  }

  return (
    <Timeline
      timeline={timeline}
      handle={item.handle}
      align={'left'}
      withTimelineInfo={false}
      withActionsBar={false}
      withEmojiReactions={false}
      avatar={
        isTopicOwner ? (
          <div className={callIconContainerStyles()}>
            <i className="fa fa-user-plus" />
          </div>
        ) : (
          <div className="timeline__status__line topic-avatar" />
        )
      }
      avatarType={'readonly'}
      className={cn(!isTopicOwner && 'timeline__status')}
    >
      {isTopicOwner ? (
        <div className={'add-contact-request align-left'}>
          <div className={'content'}>
            <div className={'header'}>
              <>
                <div>
                  { nameOfRequester === nameOfPersonToAdd ? (
                    <>
                      <span>{nameOfPersonToAdd}&nbsp;</span>
                      <span>would like to be added&nbsp;</span>
                    </>
                  ) : (
                    <>
                      <span>{nameOfRequester}&nbsp;</span>
                      <span>would like to add</span>
                      <b className={overflowStyles()}>
                        &nbsp;
                        {nameOfPersonToAdd}
                        &nbsp;
                      </b>
                    </>
                  )}
                  <span>to this topic</span>
                  {!isConfirmed && <span>, pending approval</span>}
                </div>
                <div>
                  <div className="text-muted small pt-2">
                    Alternatively, create a new topic if you don&lsquo;t want
                    them to read any previous messages.
                  </div>
                </div>
              </>
            </div>
            <Stack
              direction="row"
              gap={3}
              className={cn('body', actionsContainerStyles())}
            >
              <Button onClick={reject}>Dismiss</Button>
              <Button variant={'primary'} onClick={accept} className={acceptButton()}>
                <span className={overflowStyles()}>{`Add ${nameOfPersonToAdd}`}</span>
              </Button>
            </Stack>
          </div>
        </div>
      ) : (
        <div>
          { nameOfRequester === nameOfPersonToAdd ? (
            <>
              <span>{nameOfPersonToAdd}&nbsp;</span>
              <span>would like to be added&nbsp;</span>
            </>
          ) : (
            <>
              <span>{nameOfRequester} would like to add</span>
              <b>&nbsp;{nameOfPersonToAdd}&nbsp;</b>
            </>
          )}
          <span>to this topic, pending approval from {nameOfTopicOwner}</span>
        </div>
      )}
    </Timeline>
  )
}

AddContactRequest.propTypes = {
  timeline: PropTypes.object.isRequired,
}

const actionsContainerStyles = css({
  width: '100%',

  button: {
    width: '50%',
  },
})

const callIconContainerStyles = css({
  minWidth: 42,
  height: 42,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: '$grey300',
  borderRadius: '$3',

  path: {
    fill: 'white',
  },
})

const acceptButton = css({
  display: 'block !important',
  overflow: 'hidden !important',
  textOverflow: 'ellipsis !important',
})

const overflowStyles = css({
  maxWidth: '20ch',
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  verticalAlign: 'bottom',
})
